import { enginePackageFor } from '@/entities/template';
import { buildGraph, buildRuntimeTree, type NodeId } from '@/features/survey/engine';
import { answerSetFrom } from '@/features/survey/model/records';
import { setPreference } from '@/shared/lib';
import type { TenantDatabase } from '@/shared/offline';

/**
 * De weg van het rapport terug naar de opname.
 *
 * Elke bevinding en elk onderdeel in het rapport draagt het `sectionPath` van de
 * knoop waar het vandaan komt: het boompad als 1-based sibling-index (`13.2.1`).
 * Beide engines nummeren zo, dus dat pad wijst in de uitgeklapte boom van de
 * opname precies één knoop aan - ook binnen een multi-instantie ("Balkon 2").
 *
 * De opname zelf kent geen diepe link in het adres: ze onthoudt het gekozen
 * onderdeel in een voorkeur, en begint daar als je haar opent. Dit bestand zet
 * die voorkeur klaar; het opnamescherm blijft ongemoeid.
 */

/**
 * De voorkeur waarin de opname het gekozen onderdeel bewaart.
 *
 * Deze naam staat ook in `features/survey/hooks/useSurveyState.ts`
 * (`usePreference('opname-onderdeel:…')`). Verandert hij daar, dan hier mee.
 */
export function surveyNodePreference(inspectionUuid: string): string {
    return `opname-onderdeel:${inspectionUuid}`;
}

/**
 * De knoop in de opname die bij dit boompad hoort.
 *
 * Staat het pad er niet (de vragenlijst is herzien, of het onderdeel zit in een
 * multi-instantie die er niet meer is), dan schuift dit op naar de dichtstbijzijnde
 * voorouder: `13.2.1` → `13.2` → `13`. Zo land je hooguit een niveau te hoog, nooit
 * op een willekeurig onderdeel. `null` als er niets past.
 */
export async function surveyNodeForPath(
    db: TenantDatabase,
    inspectionUuid: string,
    sectionPath: string,
): Promise<NodeId | null> {
    if (sectionPath.trim() === '') {
        return null;
    }

    const inspection = await db.inspections.get(inspectionUuid);
    const versionUuid = inspection?.templateVersionUuid ?? null;

    if (versionUuid === null) {
        return null;
    }

    const [version, document] = await Promise.all([
        db.templateVersions.get(versionUuid),
        db.packageDocuments.get(versionUuid),
    ]);
    const pkg = enginePackageFor(version, document);

    if (pkg.status !== 'ready') {
        return null;
    }

    /* Antwoorden erbij: zonder de aantallen bestaan de instanties van een multi niet. */
    const [answers, counts] = await Promise.all([
        db.answers.where('inspectionUuid').equals(inspectionUuid).toArray(),
        db.multiCounts.where('inspectionUuid').equals(inspectionUuid).toArray(),
    ]);
    const tree = buildRuntimeTree(buildGraph(pkg.pkg), answerSetFrom(answers, counts));

    const byPath = new Map<string, NodeId>();

    for (const node of tree.nodes.values()) {
        if (!byPath.has(node.sectionPath)) {
            byPath.set(node.sectionPath, node.id);
        }
    }

    for (const candidate of ancestorPaths(sectionPath)) {
        const found = byPath.get(candidate);

        if (found !== undefined) {
            return found;
        }
    }

    return null;
}

/**
 * Zet de opname klaar op dit onderdeel en zeg of dat gelukt is.
 *
 * Lukt het niet, dan gaat het scherm er alsnog heen: dan begint de opname waar
 * de inspecteur hem liet liggen, en dat is nog altijd beter dan een knop die
 * niets doet.
 */
export async function aimSurveyAt(
    db: TenantDatabase,
    inspectionUuid: string,
    sectionPath: string,
    userUuid: string,
): Promise<boolean> {
    const nodeId = await surveyNodeForPath(db, inspectionUuid, sectionPath);

    if (nodeId === null) {
        return false;
    }

    setPreference(surveyNodePreference(inspectionUuid), userUuid, nodeId);

    return true;
}

/** `13.2.1` → `13.2.1`, `13.2`, `13`. */
function ancestorPaths(sectionPath: string): string[] {
    const parts = sectionPath.split('.');

    return parts.map((_, index) => parts.slice(0, parts.length - index).join('.'));
}
